import {
  Button,
  Card,
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  Row,
  Table,
} from 'reactstrap';
import { PropostaVersaoParcelas } from 'models/PropostaVersaoParcelas';
import formatCurrency from 'utils/formatCurrency';
import { arredondaDecimais } from 'utils/arredondaDecimais';
import { FaPlusCircle, FaTrash } from 'react-icons/fa';
import Subtitle from 'views/components/Subtitle';
import { useEffect, useMemo, useState } from 'react';
import { InputAdornment, TextField } from '@material-ui/core';
import quaseIgual from 'utils/quaseIgual';
import { iAcessoPrograma, iPropostaDados } from '../..';
import { Td, Th } from '../../styles';
import { Label } from '../../../Resumo/style';
import ParcelasProduto from './components/ParcelasProduto';

interface iPropostaVersaoParcelas extends PropostaVersaoParcelas {
  dias?: number;
  calculo?: number;
}

interface ParcelaInput {
  id: number;
  propostaVersaoId: number;
  meioPagamentoId: number;
  descricao: string;
  percentual: number;
  valorCentavos: number;
  observacao: string;
}

export default function Parcelas({
  propostaVersao,
  isModoSimulacao,
  onChange,
  isAnalisador,
  acessoPrograma,
  setCustoChanged,
  erroPosSalvar,
  isLoading = false,
}: {
  propostaVersao: iPropostaDados;
  isModoSimulacao: boolean;
  isAnalisador: boolean | false;
  acessoPrograma?: iAcessoPrograma;
  setCustoChanged?: React.Dispatch<React.SetStateAction<boolean>>;
  onChange: (a: iPropostaDados) => void;
  erroPosSalvar?: boolean;
  isLoading?: boolean;
}): JSX.Element {
  const [formatMap, setFormatMap] = useState<Record<number, boolean>>({});
  const EPSILON = 1e-6; // tolerância, pois o número pode ser perto de zero e nunca ser zero

  // Parcelas que estão vinculadas a proposta e não ao setor de atividade
  const parcelasUsar =
    propostaVersao.parcelas?.filter(item => item?.setorAtividade == null) ?? [];

  const [valoresDigitados, setValoresDigitados] = useState<
    Record<number, string>
  >({});

  const getFormat = (parcelaId: number) => {
    return formatMap[parcelaId] ?? true;
  };

  const setFormat = (parcelaId: number, value: boolean) => {
    setFormatMap(prev => ({
      ...prev,
      [parcelaId]: value,
    }));
  };

  function updateCusto(parcelas: any[], parcAprovada = false) {
    if (setCustoChanged) {
      setCustoChanged(true);
    }
    onChange({
      ...propostaVersao,
      parcelas,
      aprovacao: {
        ...propostaVersao.aprovacao,
        parcelaAprovada: parcAprovada,
      },
    });
  }

  function handleRemoverParcela(parcela: any) {
    const objMax =
      parcelasUsar && parcelasUsar.length > 0
        ? parcelasUsar?.reduce((max, curren) =>
            max.id > curren.id ? max : curren,
          )
        : null;

    const valFinalCentavos = Math.round(
      propostaVersao.valores.valorFinalParcelas * 100,
    );

    const ocorrenciaDoValor = parcelasUsar.filter(
      item => item.meioPagamentoId === parcela.meioPagamentoId,
    );

    const newArrayParcelas = parcelasUsar.filter(
      item => item.id !== parcela.id,
    );

    const isParalelo = propostaVersao.meioPagamento.meioPagamento === 4;
    const isComFinanciamento = propostaVersao.meioPagamento.meioPagamento === 3;
    const valorTotalProposta = propostaVersao.valores.valorFinalParcelas ?? 0;

    const parcelasRecursoProprio = newArrayParcelas
      .filter(item => item.meioPagamentoId === 1)
      .map(item => {
        return {
          ...item,
          valor:
            item.valor != null
              ? Math.round(item.valor * 100)
              : Math.round((valFinalCentavos * item.percentual) / 100),
        };
      });

    const newValorSemRecursoProprio =
      valFinalCentavos -
      parcelasRecursoProprio.reduce((acc, it) => acc + it.valor, 0);

    const parcelasFinanciamento = newArrayParcelas
      .filter(item => item.meioPagamentoId === 2)
      .map(item => {
        const valor = isComFinanciamento
          ? newValorSemRecursoProprio
          : valFinalCentavos;
        return {
          ...item,
          valor: Math.round((valor * item.percentual) / 100),
        };
      });

    if (
      propostaVersao.meioPagamento.meioPagamento === 3 &&
      ocorrenciaDoValor.length === 1
    ) {
      if (ocorrenciaDoValor[0].meioPagamentoId === 1) {
        parcelasRecursoProprio.push({
          id: (objMax?.id ?? 0) + 1,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          propostaVersaoId: propostaVersao.propostaVersaoId,
          meioPagamentoId: 1,
          meioPagamento: {
            descricao: 'Recurso Próprio',
          },
          dataPrevista: null,
          percentual: 0,
          valor: 0,
          observacao: ' - ',
        });
      } else {
        parcelasRecursoProprio.push({
          id: (objMax?.id ?? 0) + 2,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          propostaVersaoId: propostaVersao.propostaVersaoId,
          meioPagamentoId: 2,
          meioPagamento: {
            descricao: 'Financiamento',
          },
          dataPrevista: null,
          percentual: 0,
          valor: 0,
          observacao: ' - ',
        });
      }
    }

    const parcelasEmCentavos = [
      ...parcelasRecursoProprio,
      ...parcelasFinanciamento,
    ];

    const parcelasEmReais = parcelasEmCentavos.map(item => ({
      ...item,
      valor: +(item.valor / 100).toFixed(2), // transforma em reais
    }));

    updateCusto(parcelasEmReais);
  }

  function handleAdicionarParcela(meioPagamentoId: number) {
    if (!meioPagamentoId) return;
    const objMax =
      parcelasUsar && parcelasUsar.length > 0
        ? parcelasUsar?.reduce((max, curren) =>
            max.id > curren.id ? max : curren,
          )
        : null;

    updateCusto([
      ...parcelasUsar,
      {
        id: (objMax?.id ?? 0) + 1,
        propostaVersaoId: propostaVersao.propostaVersaoId,
        meioPagamentoId,
        dataPrevista: null,
        percentual: null,
        valor: 0,
        observacao: ' - ',
      },
    ]);
  }

  function calcularPorcentagem(valor: number, porcentagem: number): number {
    return (valor * porcentagem) / 100;
  }

  function handleChangeValueRecurso(
    parcelaId: number,
    name: string,
    value: number | string,
    habilitaFinanciamento: boolean,
  ) {
    const isParalelo = propostaVersao.meioPagamento.meioPagamento === 4;
    const isComFinanciamento = propostaVersao.meioPagamento.meioPagamento === 3;
    const valorTotalProposta = propostaVersao.valores.valorFinalParcelas ?? 0;
    const isRecursoProprio =
      parcelasUsar.find(it => it.id === parcelaId)?.meioPagamentoId === 1;

    const outrasParcelas =
      propostaVersao.parcelas.filter(item => item.setorAtividade != null) ?? [];

    if (name === 'dataPrevista' || name === 'observacao') {
      const newPropostaVersaoParcelas =
        parcelasUsar?.map(propostaVersaoParcela =>
          propostaVersaoParcela.id === parcelaId
            ? {
                ...propostaVersaoParcela,
                [name]: value,
              }
            : propostaVersaoParcela,
        ) ?? [];
      const manterValorParcAprovada =
        name === 'observacao'
          ? propostaVersao.aprovacao.parcelaAprovada
          : false;
      updateCusto(
        [...outrasParcelas, ...newPropostaVersaoParcelas],
        manterValorParcAprovada,
      );
      return;
    }

    const novaPropostaVersaoParcelas =
      parcelasUsar?.map(propostaVersaoParcela => {
        if (propostaVersaoParcela.id !== parcelaId)
          return propostaVersaoParcela;

        const recursoProprioValue = !isParalelo
          ? parcelasUsar
              .filter(item => item.meioPagamentoId === 1)
              .reduce(
                (acc, it) =>
                  acc + (valorTotalProposta * (it.percentual ?? 0)) / 100,
                0,
              )
          : 0;

        let novoValor = 0;
        let novoPercentual = 0;

        const base = isRecursoProprio
          ? valorTotalProposta
          : valorTotalProposta - recursoProprioValue;

        if (name === 'valor') {
          novoValor = Number(value ?? 0);
          // novoPercentual = (Number(value ?? 0) / base) * 100;
          novoPercentual = (Number(value ?? 0) * 100) / base;
          console.log('mudando', novoValor, base, novoPercentual);
        } else if (name === 'percentual') {
          novoPercentual = Number(value);
          novoValor = (novoPercentual * base) / 100;
        }

        return {
          ...propostaVersaoParcela,
          valor: novoValor,
          percentual: novoPercentual,
        };
      }) ?? [];

    if (isComFinanciamento) {
      // Calcula o novo recurso próprio após a atualização
      const recursoProprio = novaPropostaVersaoParcelas
        .filter(item => item.meioPagamentoId === 1)
        .reduce(
          (acc, it) => acc + (valorTotalProposta * (it.percentual ?? 0)) / 100,
          0,
        );

      const totalParcelas = valorTotalProposta - recursoProprio;

      const novaLista = novaPropostaVersaoParcelas.map(parcela => {
        if (parcela.meioPagamentoId === 1) return parcela;

        return {
          ...parcela,
          valor: arredondaDecimais(
            (totalParcelas * (parcela.percentual ?? 0)) / 100,
            2,
          ),
        };
      });

      // updateCusto(novaLista);
      updateCusto([...outrasParcelas, ...novaLista]);
    } else if (isParalelo) {
      updateCusto([...outrasParcelas, ...novaPropostaVersaoParcelas]);
      // updateCusto(novaPropostaVersaoParcelas);
    } else {
      // updateCusto(novaPropostaVersaoParcelas);
      updateCusto([...outrasParcelas, ...novaPropostaVersaoParcelas]);
    }
  }

  function criaMeioPagamento(meioPagamento: any[], mostrarSinal = false) {
    const valorTotalParcelas =
      meioPagamento.reduce((acc, it) => acc + it.valor, 0) ?? 0;

    const valorTotalProposta = propostaVersao.valores.valorFinalParcelas ?? 0;

    const meioPagamentoId = propostaVersao.meioPagamento.meioPagamento;

    const isParcelamentoEspecial =
      propostaVersao.outros?.isParcelamentoEspecial;

    let sinal =
      (isParcelamentoEspecial
        ? valorTotalProposta
        : propostaVersao.valores.valorFinalComOutros) - valorTotalParcelas;

    if (meioPagamentoId === 3) {
      // Aqui, preciso validar o cenário de proporção.

      const valorTotalParcelasCRP =
        parcelasUsar.reduce((acc, it) => acc + it.valor, 0) ?? 0;

      sinal = propostaVersao.valores.valorFinal - valorTotalParcelasCRP;
    }

    const tolerancia = 0.01; // 1 centavo

    const isQuaseIgual = quaseIgual(sinal, tolerancia);

    const valorSaldo = isQuaseIgual ? 0 : sinal;
    return (
      <>
        <Row style={{ width: '100%' }} className="mt-4">
          <Col sm="6">
            <strong>
              {meioPagamento?.[0]?.meioPagamento?.descricao ?? ''}
            </strong>
          </Col>
          <Col sm="6">
            <Button
              outline
              className="btn btn-default float-right"
              title="Adicionar Parcela"
              type="button"
              color="default"
              size="sm"
              hidden={!isModoSimulacao}
              disabled={isLoading}
              onClick={() =>
                handleAdicionarParcela(meioPagamento?.[0]?.meioPagamentoId ?? 0)
              }
            >
              <FaPlusCircle />
            </Button>
          </Col>
        </Row>
        <Table
          responsive
          bordered
          className="mt-2"
          style={{ tableLayout: 'fixed', width: '100%' }}
          overflow="hidden"
        >
          <colgroup>
            <col style={{ width: isAnalisador ? `9%` : `7%` }} />
            <col style={{ width: isAnalisador ? `22%` : `20%` }} />
            <col style={{ width: `${isModoSimulacao ? 14 : 20}%` }} />
            <col style={{ width: isAnalisador ? `20%` : `18%` }} />
            <col
              hidden={propostaVersao.usuario.isIsentoComissao}
              style={{
                width: `${propostaVersao.usuario.isIsentoComissao ? 0 : 15}%`,
              }}
            />
            <col hidden={isAnalisador} style={{ width: `16%` }} />
            <col hidden={!isModoSimulacao} style={{ width: `6%` }} />
          </colgroup>
          <thead>
            <tr>
              <Th>Dias</Th>
              <Th>Data</Th>
              <Th>%</Th>
              <Th>Valor</Th>
              <Th hidden={propostaVersao.usuario.isIsentoComissao}>
                Comissão (
                {(propostaVersao.valores.percentualComissao ?? 0) +
                  (propostaVersao.valores.percentualAjusteComissao ?? 0)}
                %)
              </Th>
              <Th hidden={isAnalisador}>Evento</Th>
              <Th hidden={!isModoSimulacao} />
            </tr>
          </thead>
          <tbody>
            {meioPagamento.map((parcela, index) => {
              const { id, dataPrevista, observacao, percentual, valor } =
                parcela;
              const { dataEmbarque } = propostaVersao.valores;

              const dataParcelaPrevista = `${
                String(dataPrevista)?.split('T')[0]
              }T21:00:00`;

              const dataPrevistaSemHora = dataPrevista?.split('T')[0] ?? null;

              let diferencaDias = 0;
              if (!dataPrevista || !dataEmbarque) diferencaDias = 0;
              else {
                const diferenca =
                  new Date(dataPrevista).getTime() -
                  new Date(dataEmbarque).getTime();

                if (diferenca <= 0) diferencaDias = 0;
                else diferencaDias = diferenca / (1000 * 3600 * 24);
              }

              return (
                <tr key={index}>
                  <Td className="text-center">
                    <Label
                      className={`${
                        isAnalisador ? `pt-3` : `pt-3 pr-4`
                      } text-muted`}
                    >
                      <strong>{Math.ceil(diferencaDias)}</strong>
                    </Label>
                  </Td>
                  <Td className="text-center">
                    <Label
                      className={`${
                        isAnalisador ? `` : `pt-3 pr-4`
                      } text-muted`}
                    >
                      {isModoSimulacao ? (
                        <TextField
                          type="date"
                          className="sm-8 red"
                          value={dataPrevistaSemHora ?? ''}
                          onChange={event =>
                            handleChangeValueRecurso(
                              id,
                              'dataPrevista',
                              String(event.target.value),
                              false,
                            )
                          }
                          disabled={!isModoSimulacao || isLoading}
                          fullWidth
                          helperText={
                            dataPrevistaSemHora === null &&
                            !isParcelamentoEspecial
                              ? 'Obrigatório.'
                              : ''
                          }
                          error={
                            dataPrevistaSemHora === null &&
                            !isParcelamentoEspecial
                          }
                        />
                      ) : (
                        <strong>
                          {dataPrevista
                            ? new Date(dataParcelaPrevista).toLocaleDateString()
                            : ''}
                        </strong>
                      )}
                    </Label>
                  </Td>
                  <Td className="text-center">
                    <Label
                      className={`${
                        isAnalisador ? `` : `pt-3 pr-4`
                      } text-muted`}
                    >
                      {isModoSimulacao ? (
                        <TextField
                          type="number"
                          className="sm-8 red"
                          value={String(percentual) ?? ''}
                          onChange={event =>
                            handleChangeValueRecurso(
                              id,
                              'percentual',
                              event.target.value,
                              false,
                            )
                          }
                          disabled={!isModoSimulacao || isLoading}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                            style: { padding: 10, width: '100%' },
                          }}
                          fullWidth
                          helperText={
                            (percentual === 0 || percentual === null) &&
                            !isParcelamentoEspecial
                              ? 'Obrigatório.'
                              : ''
                          }
                          error={
                            (percentual === 0 || percentual === null) &&
                            !isParcelamentoEspecial
                          }
                        />
                      ) : (
                        <strong>
                          {String(arredondaDecimais(percentual))}%
                        </strong>
                      )}
                    </Label>
                  </Td>
                  <Td className="text-center">
                    {isModoSimulacao ? (
                      <Label
                        className={`${
                          isAnalisador ? `` : `pt-3 pr-4`
                        } text-muted`}
                      >
                        <TextField
                          type="text"
                          value={
                            valoresDigitados[parcela.id] ??
                            (getFormat(parcela.id)
                              ? formatCurrency(parcela.valor ?? 0, 2)
                              : String(parcela.valor ?? '').replace('.', ','))
                          }
                          onChange={e => {
                            const texto = e.target.value ?? '0';
                            setValoresDigitados(prev => ({
                              ...prev,
                              [parcela.id]: texto,
                            }));
                            setFormat(parcela.id, false);
                          }}
                          onBlur={e => {
                            const texto = e.target.value ?? '0';

                            const convertido = texto
                              .replaceAll('.', '')
                              .replaceAll(',', '.')
                              .replace('R$', '')
                              .trim();

                            handleChangeValueRecurso(
                              parcela.id,
                              'valor',
                              Number(convertido),
                              false,
                            );

                            setValoresDigitados(prev => {
                              const updated = { ...prev };
                              delete updated[parcela.id]; // limpa o estado temporário
                              return updated;
                            });

                            setFormat(parcela.id, true);
                          }}
                          disabled={!isModoSimulacao || isLoading}
                        />
                      </Label>
                    ) : (
                      <Label
                        className={`${
                          isAnalisador ? `` : `pt-3 pr-4`
                        } text-muted`}
                      >
                        <strong>{formatCurrency(valor, 2)}</strong>
                      </Label>
                    )}
                  </Td>
                  {/**
                     *
                    <Td className="text-right">
                    <Label className="pt-3 pr-4 text-muted">
                    <strong>{formatCurrency(valor, 2)}</strong>
                    </Label>
                    </Td>
                    */}
                  <Td
                    className="text-right"
                    hidden={propostaVersao.usuario.isIsentoComissao}
                  >
                    <Label
                      className={`${
                        isAnalisador ? `pt-3` : `pt-3 pr-4`
                      } text-muted`}
                    >
                      <strong>
                        {formatCurrency(
                          calcularPorcentagem(
                            valor,
                            (propostaVersao.valores.percentualComissao ?? 0) +
                              (propostaVersao.valores
                                .percentualAjusteComissao ?? 0),
                          ),
                          2,
                        )}
                      </strong>
                    </Label>
                  </Td>
                  <Td hidden={isAnalisador}>
                    <Label
                      className={`${
                        isAnalisador ? `` : `pt-3 pr-4`
                      } text-muted`}
                      style={{ width: '100%', display: 'block' }}
                    >
                      {isModoSimulacao ? (
                        <TextField
                          type="text"
                          className="sm-8 red"
                          value={String(observacao) ?? ''}
                          onChange={event =>
                            handleChangeValueRecurso(
                              id,
                              'observacao',
                              String(event.target.value),
                              false,
                            )
                          }
                          disabled={!isModoSimulacao || isLoading}
                          InputProps={{ style: { padding: 10, width: '100%' } }}
                          fullWidth
                          helperText={
                            String(observacao) === '' &&
                            erroPosSalvar &&
                            !isParcelamentoEspecial
                              ? 'Obrigatório.'
                              : ''
                          }
                          error={
                            String(observacao) === '' &&
                            erroPosSalvar &&
                            !isParcelamentoEspecial
                          }
                        />
                      ) : (
                        <p
                          style={{
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            width: '100%',
                            margin: 0,
                          }}
                        >
                          {observacao}
                        </p>
                      )}
                    </Label>
                  </Td>
                  <Td hidden={!isModoSimulacao} className="text-center">
                    <Label
                      className={`${
                        isAnalisador ? `` : `pt-3 pr-4`
                      } text-muted`}
                    >
                      <InputGroup>
                        <Button
                          outline
                          className="btn-icon btn-2 ml-1"
                          color="danger"
                          type="button"
                          title="Remover Parcela"
                          size="sm"
                          disabled={isLoading}
                          onClick={() => handleRemoverParcela(parcela)}
                        >
                          <FaTrash />
                        </Button>
                      </InputGroup>
                    </Label>
                  </Td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <Td className="text-right" colSpan={3}>
                <Label
                  className={`${isAnalisador ? `` : `pt-3 pr-4`} text-muted`}
                >
                  <strong>
                    {arredondaDecimais(
                      meioPagamento.reduce(
                        (total: number, registro: any) =>
                          // eslint-disable-next-line prettier/prettier
                          total + (registro.percentual ?? 0),
                        0,
                      ),
                    )}
                    %
                  </strong>
                </Label>
              </Td>
              <Td className="text-left" colSpan={3}>
                <Label
                  className={`${isAnalisador ? `` : `pt-3 pr-4`} text-muted`}
                >
                  <strong>
                    Total:{' '}
                    {formatCurrency(
                      meioPagamento.reduce(
                        (total: number, registro: any) =>
                          // eslint-disable-next-line prettier/prettier
                          total + (registro.valor ?? 0) * 100,
                        0,
                      ) / 100,
                      2,
                    )}
                  </strong>
                </Label>
              </Td>
            </tr>

            {mostrarSinal && Math.abs(valorSaldo) > EPSILON && isModoSimulacao && (
              <tr>
                <Td colSpan={2} />
                <Td className="text-left" colSpan={3}>
                  <Label
                    className={`${isAnalisador ? `` : `pt-3 pr-4`} text-muted`}
                  >
                    <strong>
                      Saldo:{' '}
                      <span
                        style={{
                          color: valorSaldo >= 0 ? '' : 'red',
                        }}
                      >
                        R$ {valorSaldo >= 0 ? '-' : '+'}
                        {formatCurrency(
                          Math.abs(arredondaDecimais(valorSaldo)),
                          2,
                          false,
                          false,
                        )}
                      </span>
                    </strong>
                  </Label>
                </Td>
              </tr>
            )}
          </tfoot>
        </Table>
      </>
    );
  }

  function criarTabela(propostaVersaoParcelasArg: any[]) {
    const recursoProprio = propostaVersaoParcelasArg.filter(
      parc => parc.meioPagamentoId === 1,
    );
    const financiamento = propostaVersaoParcelasArg.filter(
      parc => parc.meioPagamentoId === 2,
    );

    const tabelas: JSX.Element[] = [];
    if (recursoProprio.length > 0) {
      const mostrarSinal = !(propostaVersao.meioPagamento.meioPagamento === 3);
      tabelas.push(criaMeioPagamento(recursoProprio, mostrarSinal));
    }

    if (financiamento.length > 0) {
      tabelas.push(criaMeioPagamento(financiamento, true));
    }

    return tabelas;
  }

  return (
    <Card className="shadow p-3">
      <Subtitle name="Parcelamento" />
      <small>
        {propostaVersao.aprovacao.parcelaAprovada
          ? 'Parcelas aprovadas.'
          : 'Se parcelas estiverem fora do padrão, passarão por aprovação.'}
      </small>
      <small
        hidden={!propostaVersao.outros?.isParcelamentoEspecial}
        style={{
          color: 'red',
        }}
      >
        Se for informada uma parcela por setor de atividade, o meio de pagamento
        será alterado para recurso próprio e as parcelas preenchidas serão
        perdidas.
      </small>
      {propostaVersao.outros?.isRegramentoGeral && (
        <ParcelasProduto
          propostaVersao={propostaVersao}
          onChange={onChange}
          isModoSimulacao={isModoSimulacao}
          isAnalisador={isAnalisador}
        />
      )}

      {criarTabela(parcelasUsar)}
    </Card>
  );
}
